import { useEffect, useRef, type RefObject } from 'react'
import type { GeneralChatMessage } from '../types'

/** How close to the bottom counts as reading the newest message (dispatch-help's thread uses the same). */
const PINNED_PX = 40

/** The space left above a new user message brought to the top of the thread. */
const ANCHOR_GAP_PX = 16

/** The attribute a user message's element carries, so the thread can find the newest one. */
const USER_MESSAGE_ATTRIBUTE = 'data-user-message'

const userCountOf = (messages: GeneralChatMessage[]): number => messages.filter((message) => message.message_type === 'user').length

interface ThreadScroll {
    scroller: RefObject<HTMLDivElement>
    onScroll: () => void
}

/**
 * Where the thread's view goes as things come in (v17). A new user message (typed, or a suggestion's question) is brought to the top of
 * the view, and kept there while its answer arrives under it, so the user reads the answer from its top, never the previous answer or the
 * end of a long table. Otherwise the view follows the bottom while the user is at it; scrolled up to read, they stay where they are.
 * `changes` are what moves the content (the reply, the card results, a question, the held notices).
 */
export function useThreadScroll(tabId: string | null, messages: GeneralChatMessage[], changes: unknown[]): ThreadScroll {
    const scroller = useRef<HTMLDivElement>(null)
    // Whether the user is at the bottom, so the thread follows what comes in.
    const pinned = useRef(true)
    // Whether the view holds the newest user message at its top (until the user scrolls it themselves).
    const anchored = useRef(false)
    const lastTop = useRef(0)
    // The user messages already seen: one more is a new message to bring into view; null takes the next count as it is.
    const userCount = useRef<number | null>(null)
    const lastTab = useRef(tabId)

    // Another chat opens at its newest message. A fresh chat taking its first tab is the same thread, so its new message stays in view.
    useEffect(() => {
        const previous = lastTab.current
        lastTab.current = tabId
        if (previous === null || previous === tabId) return
        pinned.current = true
        anchored.current = false
        lastTop.current = 0
        userCount.current = null
    }, [tabId])

    const users = userCountOf(messages)
    useEffect(() => {
        // Exactly one more: a message just sent or asked. More at once is a thread read in full, which opens at its bottom.
        if (userCount.current !== null && users === userCount.current + 1) {
            anchored.current = true
            pinned.current = false
        }
        userCount.current = users
        const el = scroller.current
        if (!el) return
        if (anchored.current) {
            const targets = el.querySelectorAll<HTMLElement>(`[${USER_MESSAGE_ATTRIBUTE}]`)
            const target = targets[targets.length - 1]
            if (target) {
                const top = target.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop - ANCHOR_GAP_PX
                el.scrollTop = Math.max(0, top)
                lastTop.current = el.scrollTop
                return
            }
        }
        if (pinned.current) el.scrollTop = el.scrollHeight
        // The content's changes are what this follows; the list of them is the caller's.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users, messages, ...changes])

    // The user's own scroll ends the anchor. Scrolling up leaves the bottom; coming back within `PINNED_PX` of it follows again. The distance
    // is read first: content that shrinks at the bottom (an answered card pruned for a shorter reply) clamps `scrollTop` and fires a
    // scroll that looks like scrolling up, but the view is still at the bottom.
    const onScroll = () => {
        const el = scroller.current
        if (!el) return
        if (anchored.current) {
            // The view's own move (to the anchor) fires a scroll too: only a scroll away from it is the user's.
            if (Math.abs(el.scrollTop - lastTop.current) <= 2) return
            anchored.current = false
        }
        if (el.scrollHeight - el.scrollTop - el.clientHeight < PINNED_PX) pinned.current = true
        else if (el.scrollTop < lastTop.current - 2) pinned.current = false
        lastTop.current = el.scrollTop
    }

    return { scroller, onScroll }
}
